/**
 * Tracks whether Segment events can reach Segment, for the
 * `X-Segment-Disabled` header on `/api/v3/search/`.
 *
 * Two hosts must both work (see `./segment`): the SDK loads from `cdnURL`, and
 * events post to `apiHost`. Blocklists often take the collection host and leave
 * the CDN alone, so delivery is probed rather than inferred from the load.
 */
import { logExceptionOnce } from '@hungryroot/o11y';
import { withTimeout } from './utils/withTimeout';

const DEFAULT_TIMEOUT_MS = 8000;

let loadOk: boolean | undefined;
let deliveryOk: boolean | undefined;

/**
 * `true` if either check failed, `false` only once both passed, `undefined`
 * until enough is known. Asymmetric because either failure alone strands every
 * event. Callers must not collapse `undefined` into `false` — it omits the
 * header, which the back end logs as `null`.
 */
export function getIsSegmentDisabled(): boolean | undefined {
  if (loadOk === false || deliveryOk === false) {
    return true;
  }
  if (loadOk === true && deliveryOk === true) {
    return false;
  }
  return undefined;
}

/**
 * A check that outlives its timeout can still succeed later — a slow load
 * flushes its buffered events once it resolves — so a late success must be able
 * to revise the verdict rather than leaving it stuck at disabled.
 */
function reviseOnLateSuccess(
  work: PromiseLike<unknown>,
  mark: () => void
): void {
  void Promise.resolve(work).then(mark, () => {});
}

export async function observeSegmentLoad(
  load: PromiseLike<unknown>,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  try {
    await withTimeout(load, timeoutMs, 'Segment load');
    loadOk = true;
  } catch (error) {
    loadOk = false;
    logExceptionOnce(error, { key: 'segment.load.unreachable' });
    reviseOnLateSuccess(load, () => {
      loadOk = true;
    });
  }
}

/**
 * `mode: 'no-cors'` makes the response opaque, which is fine — a blocked
 * request rejects and a delivered one resolves, and that needs no CORS
 * cooperation from the collector. Network failures land in the same `catch`
 * deliberately: either way events are not arriving.
 */
export async function probeSegmentDelivery(
  url: string,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const probe = fetch(url, {
    method: 'HEAD',
    mode: 'no-cors',
    cache: 'no-store',
  });

  try {
    await withTimeout(probe, timeoutMs, 'Segment delivery probe');
    deliveryOk = true;
  } catch (error) {
    deliveryOk = false;
    logExceptionOnce(error, { key: 'segment.delivery.unreachable' });
    reviseOnLateSuccess(probe, () => {
      deliveryOk = true;
    });
  }
}
